/**
 * GridCrash — Pinch-Zoom Viewport (Expert boards)
 *
 * Two-finger pinch-to-zoom and pan for the 16×30 Expert grid inside
 * a fixed, overflow-hidden viewport (.board-viewport--zoomable).
 *
 * Single-finger input is left alone so taps still reach the board's
 * own pointer handlers. As soon as a second finger touches down, all
 * pointer events are captured here (stopPropagation in the capture
 * phase) so the board never misreads a pinch as a tap.
 */

const MIN_SCALE = 1;
const MAX_SCALE = 3;

interface Point { x: number; y: number }

export class PinchZoom {
  private viewport: HTMLElement;
  private content:  HTMLElement;

  private pointers = new Map<number, Point>();
  private gestureActive = false;

  // Current transform
  private scale = 1;
  private tx = 0;
  private ty = 0;

  // Gesture-start snapshot
  private startScale = 1;
  private startTx = 0;
  private startTy = 0;
  private startDist = 0;
  private startMid: Point = { x: 0, y: 0 };

  constructor(viewport: HTMLElement, content: HTMLElement) {
    this.viewport = viewport;
    this.content = content;
    // Capture phase so a two-finger gesture wins over board handlers.
    viewport.addEventListener("pointerdown",   this.onDown,   { capture: true });
    viewport.addEventListener("pointermove",   this.onMove,   { capture: true });
    viewport.addEventListener("pointerup",     this.onUp,     { capture: true });
    viewport.addEventListener("pointercancel", this.onUp,     { capture: true });
  }

  destroy(): void {
    this.viewport.removeEventListener("pointerdown",   this.onDown,   { capture: true });
    this.viewport.removeEventListener("pointermove",   this.onMove,   { capture: true });
    this.viewport.removeEventListener("pointerup",     this.onUp,     { capture: true });
    this.viewport.removeEventListener("pointercancel", this.onUp,     { capture: true });
    this.content.style.transform = "";
  }

  reset(): void {
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.apply();
  }

  private onDown = (e: PointerEvent): void => {
    if (e.pointerType === "mouse") return;
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this.pointers.size === 2) {
      this.gestureActive = true;
      const [a, b] = [...this.pointers.values()];
      this.startDist  = Math.hypot(b.x - a.x, b.y - a.y) || 1;
      this.startMid   = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      this.startScale = this.scale;
      this.startTx    = this.tx;
      this.startTy    = this.ty;
      e.stopPropagation();
    }
  };

  private onMove = (e: PointerEvent): void => {
    if (!this.pointers.has(e.pointerId)) return;
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (!this.gestureActive || this.pointers.size < 2) return;

    e.stopPropagation();
    e.preventDefault();

    const [a, b] = [...this.pointers.values()];
    const dist = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const mid: Point = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };

    const newScale = this.clampScale(this.startScale * (dist / this.startDist));

    // Keep the point under the gesture midpoint stationary while
    // scaling, then add the two-finger pan delta.
    const rect = this.viewport.getBoundingClientRect();
    const originX = this.startMid.x - rect.left;
    const originY = this.startMid.y - rect.top;
    const ratio = newScale / this.startScale;

    this.scale = newScale;
    this.tx = originX - ratio * (originX - this.startTx) + (mid.x - this.startMid.x);
    this.ty = originY - ratio * (originY - this.startTy) + (mid.y - this.startMid.y);

    this.clampTranslation();
    this.apply();
  };

  private onUp = (e: PointerEvent): void => {
    if (!this.pointers.delete(e.pointerId)) return;
    if (this.gestureActive) {
      e.stopPropagation();
      if (this.pointers.size < 2) {
        this.gestureActive = false;
        // Snap back to rest when zoomed all the way out.
        if (this.scale <= MIN_SCALE + 0.01) this.reset();
      }
    }
  };

  private clampScale(s: number): number {
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));
  }

  /** Keep the scaled board from being panned fully out of view. */
  private clampTranslation(): void {
    const vw = this.viewport.clientWidth;
    const vh = this.viewport.clientHeight;
    const cw = this.content.offsetWidth  * this.scale;
    const ch = this.content.offsetHeight * this.scale;

    const minX = Math.min(0, vw - cw);
    const minY = Math.min(0, vh - ch);
    this.tx = Math.min(Math.max(this.tx, minX), Math.max(0, vw - cw));
    this.ty = Math.min(Math.max(this.ty, minY), Math.max(0, vh - ch));
    // When content is smaller than the viewport, allow only 0 (handled
    // by the Math.max(0, …) upper bound collapsing the range).
  }

  private apply(): void {
    this.content.style.transform =
      `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
  }
}
